import {
  signUp,
  resendSignUpCode,
  confirmSignUp,
  updateUserAttribute,
  signIn,
  signOut,
  signInWithRedirect,
  fetchAuthSession,
  getCurrentUser,
  autoSignIn,
  resetPassword,
  confirmResetPassword,
  updatePassword,
  confirmUserAttribute,
  fetchUserAttributes,
} from 'aws-amplify/auth';
import type {
  SignUpOutput,
  UpdateUserAttributeOutput,
  ResetPasswordOutput,
  SignInOutput,
  VerifiableUserAttributeKey,
} from 'aws-amplify/auth';
import { useCompareVersions } from '@/composables/useCompareVersions';
import { useResetState } from '@/composables/useResetState';
import { useUpdateUser } from '@/composables/useUpdateUser';
import { UserRepository } from '@/features/user/api/userRepository';
import { useLoginState } from './store/useLoginStore';
import { useLoadingState } from './store/useLoadingStore';
import {
  ACCOUNT_TYPES,
  COGNITO_PROVIDER_IDS,
  COGNITO_PROVIDERS,
} from '@/features/user/constants';
import { useDialogState } from './store/useDialogStore';
import { AccountType } from '~/features/user/api/types';
import { useUserState } from './store/useUserStore';

export const useCognitoAuth = () => {
  const router = useRouter();
  const repository = UserRepository();
  const {
    setHasMembershipToApp,
    clearWebViewCache,
    setLastLoginMethodToApp,
    getPendingLoginMethod,
    setPendingLoginMethodToApp,
    clearPendingLoginMethodFromApp,
  } = useFlutterConnection();
  const { update: updateUserId } = useUpdateUser();
  const { isAppSupportCognitoAuth } = useCompareVersions();
  const {
    setUseWithLogIn,
    setHeaderMemberToken,
    setIsCompleteMembershipInfo,
    userId,
    headerMemberToken,
  } = useLoginState();
  const { stopLoadings } = useLoadingState();
  const { addErrorStack } = useDialogState();
  const { resetStateOnSignOut, resetStateOnUnsubscribe } = useResetState();
  const { userCredentials, setAccountType } = useUserState();

  // エラーメッセージ
  const errorMessage = ref('');

  /**
   * ログイン成功時にログイン方法を永続化する
   */
  const saveLoginMethodOnSuccess = async () => {
    const pendingMethod = await getPendingLoginMethod();
    console.log('saveLoginMethodOnSuccess:', pendingMethod);
    if (pendingMethod) {
      await setLastLoginMethodToApp(pendingMethod);
      await clearPendingLoginMethodFromApp();
    }
  };

  // CognitoのAuthトークンをリクエストヘッダーに付与
  const _setHeaderMemberToken = async () => {
    const token = await getAccessToken();
    await setHeaderMemberToken(token);
  };

  /**
   * ユーザー名とパスワードを入力して認証コードをメールで受け取る
   * @param email メールアドレス
   * @param password パスワード
   */
  const handleSignUp = async (
    email: string,
    password: string,
    accountType: AccountType,
  ) => {
    setAccountType(accountType);
    try {
      const { nextStep } = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            'custom:account_type': accountType,
            'custom:user_id': userId.value.toString(),
          },
          autoSignIn: {
            enabled: true,
          },
        },
      });
      await _handleSignUpStep(nextStep);
    } catch (error: any) {
      error.message_jp = _getJpErrMessage(error, '新規登録');
      console.log(error);
      errorMessage.value = error.message_jp;
      stopLoadings('sign-up');
    }
  };

  /**
   * 指定メールアドレスに認証コードを再送する
   * @param email メールアドレス
   */
  const handleResendSignUpCode = async (email: string) => {
    try {
      await resendSignUpCode({
        username: email,
      });
    } catch (error: any) {
      error.message_jp = _getJpErrMessage(error, '仮登録認証コード再送信');
      console.log(error);
      errorMessage.value = error.message_jp;
    }
  };

  /**
   * サインアップの確認（メールで認証コードを受け取る場合）
   * @param email メールアドレス
   * @param confirmationCode 認証コード
   */
  const handleSignUpConfirmation = async (
    email: string,
    confirmationCode: string,
  ) => {
    try {
      const { nextStep } = await confirmSignUp({
        username: email,
        confirmationCode,
      });
      await _handleSignUpStep(nextStep);
    } catch (error: any) {
      error.message_jp = _getJpErrMessage(error, '仮登録認証');
      console.log(error);
      errorMessage.value = error.message_jp;
      stopLoadings('mail-confirmation');
    }
  };

  /**
   * ユーザー名とパスワードでサインイン
   * @param email メールアドレス
   * @param password パスワード
   */
  const handleSignIn = async (email: string, password: string) => {
    await setPendingLoginMethodToApp('email');

    try {
      const { nextStep } = await signIn({ username: email, password });
      await _handleSignInStep(nextStep);
    } catch (error: any) {
      error.message_jp = _getJpErrMessage(error, 'ログイン');
      console.log(error);
      errorMessage.value = error.message_jp;
      stopLoadings('sign-in');
    }

    // TODO: 不要?
    await getCognitoTokens();
  };

  /** オートサインイン */
  const _handleAutoSignIn = async () => {
    try {
      const { nextStep } = await autoSignIn();
      await _handleAutoSignInStep(nextStep);
    } catch (error: any) {
      error.message_jp = _getJpErrMessage(error, 'オートサインイン');
      console.log(error);
      errorMessage.value = error.message_jp;
    }
  };

  /** LINEでサインイン */
  const handleLineSignIn = async () => {
    await setPendingLoginMethodToApp('line');

    try {
      // 事前にcognito関係の認証情報が残っている場合は削除
      await signOut({ global: true });
      await signInWithRedirect({
        provider: { custom: 'LINE' },
      });
    } catch (error: any) {
      error.message_jp = _getJpErrMessage(error, 'LINEログイン');
      console.log(error);
      errorMessage.value = error.message_jp;
    }
  };

  /** Appleでサインイン */
  const handleAppleSignIn = async () => {
    await setPendingLoginMethodToApp('apple');

    try {
      // 事前にcognito関係の認証情報が残っている場合は削除
      await signOut({ global: true });
      await signInWithRedirect({
        provider: { custom: 'SignInWithApple' },
      });
    } catch (error: any) {
      error.message_jp = _getJpErrMessage(error, 'Appleログイン');
      console.log(error);
      errorMessage.value = error.message_jp;
    }
  };

  /** Googleでサインイン */
  const handleGoogleSignIn = async () => {
    await setPendingLoginMethodToApp('google');

    try {
      // 事前にcognito関係の認証情報が残っている場合は削除
      await signOut({ global: true });
      await signInWithRedirect({
        provider: 'Google',
      });
    } catch (error: any) {
      error.message_jp = _getJpErrMessage(error, 'Googleログイン');
      console.error(error);
      errorMessage.value = error.message_jp;
    }
  };

  /** LINEで連携 */
  const handleLineIntegration = async () => {
    try {
      await signInWithRedirect({
        provider: { custom: 'LINE' },
      });
    } catch (error: any) {
      error.message_jp = _getJpErrMessage(error, 'LINE連携');
      console.log(error);
      errorMessage.value = error.message_jp;
    }
  };

  /** Appleで連携 */
  const handleAppleIntegration = async () => {
    try {
      await signInWithRedirect({
        provider: { custom: 'SignInWithApple' },
      });
    } catch (error: any) {
      error.message_jp = _getJpErrMessage(error, 'Apple連携');
      console.log(error);
      errorMessage.value = error.message_jp;
    }
  };

  /** Googleで連携 */
  const handleGoogleIntegration = async () => {
    try {
      await signInWithRedirect({
        provider: 'Google',
      });
    } catch (error: any) {
      error.message_jp = _getJpErrMessage(error, 'Google連携');
      console.error(error);
      errorMessage.value = error.message_jp;
    }
  };

  /** サインインしているかどうか */
  const isSignedIn = async () => {
    if (!isAppSupportCognitoAuth()) return false;
    try {
      const user = await getCurrentUser();
      return true;
    } catch (error) {
      return false;
    }
  };

  /** ユーザー属性を取得（主にSNSログイン時にアカウントから氏名を取得するために使用） */
  const getUserAttributes = async () => {
    try {
      const attributes = await fetchUserAttributes();
      console.log('attrs', attributes);
      return attributes;
    } catch (error) {
      console.error(error);
    }
  };

  /** TOKEN類を取得 */
  const getCognitoTokens = async () => {
    try {
      const { accessToken, idToken } = (await fetchAuthSession()).tokens ?? {};
      return { accessToken, idToken };
    } catch (error: any) {
      error.message_jp = _getJpErrMessage(error, 'ログイン情報取得');
      console.error(error);
      errorMessage.value = error.message_jp;
    }
  };

  /** 特にAccessTokenを取得 */
  const getAccessToken = async () => {
    try {
      const lawTokens = await getCognitoTokens();
      const token = lawTokens?.accessToken?.toString();
      if (!token) {
        console.log(`NoToken`, token);
        return '';
      }
      return token;
    } catch (error: any) {
      console.log(`TokenError`, error);
      return '';
    }

    // if (!token)
    //   throw showError(
    //     'ログイン時にエラーが発生しました。再度アプリをインストールし直してください。',
    //   );
  };

  /** 現在ログインに使っているプロバイダーのIDを取得 */
  const getCurrentProviderId = async () => {
    try {
      const { username } = await getCurrentUser();
      const targetProvider = COGNITO_PROVIDERS.find((provider) =>
        username.includes(provider.name.toLocaleLowerCase()),
      );
      return targetProvider ? targetProvider.id : COGNITO_PROVIDER_IDS.NAVIPARK;
    } catch (error) {
      console.error(error);
      return COGNITO_PROVIDER_IDS.NAVIPARK;
    }
  };

  /**
   * アカウント区分を取得する
   * 1.バックエンドから取得を試みる -> 2.Cognito属性から取得を試みる -> 3.フォールバック(個人として)
   * @returns accountType
   */
  const getAccountType = async (): Promise<AccountType> => {
    // 1.バックエンドから取得
    const userInfo = await repository.getMemberInfo();
    const accountType = userInfo.data.value.accountType;

    if (accountType) return accountType;

    // 2.バックエンドから取得できなければCognitoから取得
    const attributes = await getUserAttributes();
    const cognitoAccountType = attributes?.['custom:account_type'];

    if (cognitoAccountType) return cognitoAccountType as AccountType;

    // 3.フォールバック
    return ACCOUNT_TYPES.PERSONAL;
  };

  /**
   * ユーザーに入力させたパスワードが正しいかどうかを確認
   * @param password パスワード
   */
  const checkPassword = async (password: string) => {
    try {
      await updatePassword({ oldPassword: password, newPassword: password });
      return true;
    } catch (error: any) {
      console.log(error);
      errorMessage.value = 'パスワードが正しくありません';
      return false;
    }
  };

  /**
   * ユーザーの会員情報を更新
   * @param attributeKey 属性名
   * @param value 値
   */
  const handleUpdateAttribute = async (attributeKey: string, value: string) => {
    try {
      const output = await updateUserAttribute({
        userAttribute: {
          attributeKey,
          value,
        },
      });
      await _handleUpdateAttributeStep(output);
    } catch (error: any) {
      error.message_jp = _getJpErrMessage(error, '会員情報更新');
      console.log(error);
      errorMessage.value = error.message_jp;
    }
  };

  /**
   * ユーザーの会員情報変更の確認（メールで認証コードを受け取る場合）
   * @param attributeKey 属性名
   * @param confirmationCode 認証コード
   */
  const handleUpdateAttributeConfirmation = async (
    userAttributeKey: VerifiableUserAttributeKey,
    confirmationCode: string,
  ) => {
    try {
      await confirmUserAttribute({ userAttributeKey, confirmationCode });
    } catch (error: any) {
      error.message_jp = _getJpErrMessage(error, 'メールアドレス認証');
      console.log(error);
      errorMessage.value = error.message_jp;
    }
  };

  /**
   * パスワードをリセット
   * @param email メールアドレス
   */
  const handleResetPassword = async (email: string) => {
    try {
      const { nextStep } = await resetPassword({ username: email });
      await _handleResetPasswordStep(nextStep);
    } catch (error: any) {
      error.message_jp = _getJpErrMessage(error, 'パスワードリセット');
      console.error(error);
      errorMessage.value = error.message_jp;
      await _handleResetPasswordStep();
    }
  };

  /**
   * リセットしたパスワードを更新（未ログイン時）
   * @param email メールアドレス
   * @param newPassword 新しいパスワード
   * @param confirmationCode 認証コード
   */
  const handleResetPasswordConfirmation = async (
    email: string,
    newPassword: string,
    confirmationCode: string,
  ) => {
    try {
      await confirmResetPassword({
        username: email,
        newPassword,
        confirmationCode,
      });
    } catch (error: any) {
      error.message_jp = _getJpErrMessage(error, 'パスワードリセット後の更新');
      console.error(error);
      errorMessage.value = error.message_jp;

      // 認証コードが不正な場合は、再度認証コード入力画面へリダイレクト
      if (
        error.name === 'ExpiredCodeException' ||
        error.name === 'CodeMismatchException'
      ) {
        router.push('/reset-password/confirm-code');
      }
    }
  };

  /**
   * パスワードを更新（ログイン中）
   * @param oldPassword 旧パスワード
   * @param newPassword 新パスワード
   */
  const handleUpdatePassword = async (
    oldPassword: string,
    newPassword: string,
  ) => {
    try {
      await updatePassword({ oldPassword, newPassword });
    } catch (error: any) {
      error.message_jp = _getJpErrMessage(error, 'パスワード更新');
      console.error(error);
      errorMessage.value = error.message_jp;
    }
  };

  /** サインアウト */
  const handleSignOut = async (redirectUrl?: string) => {
    console.log('[handleSignOut]');
    try {
      await signOut();
      await setHasMembershipToApp(false);
      resetStateOnSignOut();
      console.log(
        `[headerMemberToken]: ${headerMemberToken.value} (ここは消えている想定)`,
      );
      console.log(`[userId]: ${userId.value} (更新前)`);
      await updateUserId();
      console.log(
        `[userId]: ${userId.value} (更新後、ここでidが変わってるはず、変わってなければ黒)`,
      );
      /** Googleアカウントが自動選択されないようにする */
      await clearWebViewCache();
      if (redirectUrl) router.replace(redirectUrl);
    } catch (error: any) {
      error.message_jp = _getJpErrMessage(error, 'ログアウト');
      console.error(error);
      errorMessage.value = error.message_jp;
    }
  };

  /** 退会 */
  const handleUnsubscribe = async () => {
    try {
      await signOut();
      resetStateOnUnsubscribe();
      await Promise.all([
        // 退会後、会員でなくてもアプリを利用できるようにする
        setUseWithLogIn(false),
        setHasMembershipToApp(false),
        updateUserId(),
      ]);
    } catch (error: any) {
      error.message_jp = _getJpErrMessage(error, '退会');
      console.error(error);
      errorMessage.value = error.message_jp;
    }
  };

  /** アクセストークンの強制的な更新 */
  const forceRefreshSession = async () => {
    try {
      const authSession = await fetchAuthSession({ forceRefresh: true });
      return authSession.tokens?.accessToken.toString();
    } catch (error: any) {
      error.message_jp = _getJpErrMessage(error, 'アクセストークンの更新');
      console.error(error);
      errorMessage.value = `${error.message_jp} 再度ログインしてください`;
    }
  };

  /**
   * サインアップフローをハンドリング
   * @param step CognitoAPIの返り値 "nextStep"
   */
  const _handleSignUpStep = async (step: SignUpOutput['nextStep']) => {
    const _step = step.signUpStep;
    switch (_step) {
      case 'CONFIRM_SIGN_UP':
        // 認証コードの入力画面へリダイレクト
        router.push('/sign-up/confirm-code');
        break;

      // NOTE: 通常のユーザーが通るサインアップ後のオートサインイン処理
      case 'COMPLETE_AUTO_SIGN_IN': {
        await _handleAutoSignIn();
        console.log('サインアップ&オートサインインが完了しました');

        // 連携前にヘッダーに付与するCognitoのAuthTokenを削除
        await setHeaderMemberToken(undefined);

        try {
          const session = await fetchAuthSession();
          console.log('[useCognitoAuth.ts:L561] Cognito session status', {
            accessToken: session.tokens?.accessToken.toString(),
            idToken: session.tokens?.idToken?.toString(),
          });
        } catch (error) {
          console.log(
            '[useCognitoAuth.ts:L563] Cognito session unavailable',
            error,
          );
        }

        const tokens = await getCognitoTokens();

        console.log('[useCognitoAuth.ts:L573] Cognito session status', {
          accessToken: tokens?.accessToken?.toString(),
          idToken: tokens?.idToken?.toString(),
        });

        console.log(
          `[userId]: ${userId.value} (handleSignUpStep COMPLETE_AUTO_SIGN_IN時)`,
        );

        const { error } = await repository.connectAccount(
          tokens?.idToken?.toString() || '',
          COGNITO_PROVIDER_IDS.NAVIPARK,
        );

        if (error.value) {
          // 会員登録しないで利用するフローへ
          await setUseWithLogIn(false);
          addErrorStack({
            title: 'アカウント連携中にエラーが発生しました CA01',
            message: '引き続き会員登録が不要な機能は使うことができます',
            code: 401,
          });
          await sleep(1500); // SNSサインアウト後はリロードされてしまうため、エラーメッセージ表示のために待機
          await handleSignOut('/');
          return;
        }

        console.log('Cognito連携に成功しました');
        await Promise.all([
          setHasMembershipToApp(true),
          setUseWithLogIn(true),
          setIsCompleteMembershipInfo(undefined),
          _setHeaderMemberToken(),
          saveLoginMethodOnSuccess(),
        ]);
        await updateUserId();
        router.push('/');
        break;
      }

      // NOTE: 一度中断してサインアップを再開したユーザーが通るフロー
      case 'DONE': {
        console.log('再開したサインアップに成功しました');
        await Promise.all([
          setUseWithLogIn(true),
          setIsCompleteMembershipInfo(undefined),
          // 一度中断したらセッションが切れている状態なので、再度サインインする
          signIn({
            username: userCredentials.value?.email || '',
            password: userCredentials.value?.password || '',
          }),
        ]);

        console.log(`[userId]: ${userId.value} (handleSignUpStep DONE時)`);

        // アカウント連携
        const tokens = await getCognitoTokens();
        const { error } = await repository.connectAccount(
          tokens?.idToken?.toString() || '',
          COGNITO_PROVIDER_IDS.NAVIPARK,
        );

        if (error.value || !tokens) {
          // 会員登録しないで利用するフローへ
          await setUseWithLogIn(false);
          addErrorStack({
            title: 'アカウント連携中にエラーが発生しました CA02',
            message: '引き続き会員登録が不要な機能は使うことができます',
            code: 401,
          });
          await sleep(1500); // SNSサインアウト後はリロードされてしまうため、エラーメッセージ表示のために待機
          await handleSignOut('/');
          return;
        }
        router.push('/');
        break;
      }
      default:
        throw new Error(_step satisfies never);
    }
  };

  /**
   * サインインフローをハンドリング
   * @param step CognitoAPIの返り値 "nextStep"
   */
  const _handleSignInStep = async (step: SignInOutput['nextStep']) => {
    const _step = step.signInStep;
    switch (_step) {
      case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
        // confirmSignIn()
        break;
      case 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE':
        // confirmSignIn()
        break;
      case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
        // confirmSignIn()
        break;
      case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE':
        // confirmSignIn()
        break;
      case 'CONTINUE_SIGN_IN_WITH_EMAIL_SETUP':
        // confirmSignIn()
        break;
      case 'CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION':
        // confirmSignIn()
        break;
      case 'CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION':
        // confirmSignIn()
        break;
      case 'CONFIRM_SIGN_IN_WITH_PASSWORD':
        // confirmSignIn()
        break;
      case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP':
        // confirmSignIn()
        break;
      case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
        // confirmSignIn()
        break;
      case 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION':
        // confirmSignIn()
        break;
      case 'RESET_PASSWORD':
        // パスワードリセット画面へリダイレクト
        router.push('/reset-password/send-email');
        break;
      case 'CONFIRM_SIGN_UP':
        router.push('/sign-in/confirm-code');
        break;
      case 'DONE': {
        await Promise.all([setUseWithLogIn(true), _setHeaderMemberToken()]);
        const { error } = await updateUserId();

        // 未連携の場合
        if (error.value?.data?.code === 403) {
          // 連携前にヘッダーに付与するCognitoのAuthTokenを削除
          await setHeaderMemberToken(undefined);
          const providerId = await getCurrentProviderId();
          const tokens = await getCognitoTokens();
          const { error: e2 } = await repository.connectAccount(
            tokens?.idToken?.toString() || '',
            providerId,
          );

          if (e2.value) {
            // NOTE: 会員登録をせずに利用するフローへ誘導
            await setUseWithLogIn(false);
            addErrorStack({
              title: 'アカウント連携中にエラーが発生しました CA03',
              message: '引き続き会員登録が不要な機能は使うことができます',
              code: 401,
            });
            await handleSignOut('/');
            return;
          }

          await _setHeaderMemberToken();
          await updateUserId();
        }
        await setHasMembershipToApp(true);
        await saveLoginMethodOnSuccess();
        // NOTE: ログイン後はHOMEへリダイレクト
        router.push('/');
        break;
      }
      default:
        throw new Error(_step satisfies never);
    }
  };

  /**
   * サインアップ後のオートサインインフローをハンドリング
   * @param step CognitoAPIの返り値 "nextStep"
   */
  const _handleAutoSignInStep = async (step: SignInOutput['nextStep']) => {
    const _step = step.signInStep;
    switch (_step) {
      case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
        // confirmSignIn()
        break;
      case 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE':
        // confirmSignIn()
        break;
      case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE':
        // confirmSignIn()
        break;
      case 'CONTINUE_SIGN_IN_WITH_EMAIL_SETUP':
        // confirmSignIn()
        break;
      case 'CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION':
        // confirmSignIn()
        break;
      case 'CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION':
        // confirmSignIn()
        break;
      case 'CONFIRM_SIGN_IN_WITH_PASSWORD':
        // confirmSignIn()
        break;
      case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
        // confirmSignIn()
        break;
      case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP':
        // confirmSignIn()
        break;
      case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
        // confirmSignIn()
        break;
      case 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION':
        // confirmSignIn()
        break;
      case 'RESET_PASSWORD':
        // パスワードリセット画面へリダイレクト
        router.push('/reset-password/send-email');
        break;
      case 'CONFIRM_SIGN_UP':
        router.push('/sign-in/confirm-code');
        break;
      case 'DONE':
        console.log('オートサインインが完了しました'); /////
        break;
      default:
        throw new Error(_step satisfies never);
    }
  };

  /**
   * 登録情報更新処理をハンドリング
   * @param output CognitoAPIの返り値
   */
  const _handleUpdateAttributeStep = async (
    output: UpdateUserAttributeOutput,
  ) => {
    const _step = output.nextStep.updateAttributeStep;
    switch (_step) {
      case 'CONFIRM_ATTRIBUTE_WITH_CODE':
        console.log('認証コードで認証する必要があります');
        break;
      case 'DONE':
        // 会員情報登録完了の場合リダイレクト
        router.push('/membership/complete');
        break;
      default:
        throw new Error(_step satisfies never);
    }
  };

  /**
   * パスワードリセット処理をハンドリング
   * @param step CognitoAPIの返り値 "nextStep"
   */
  const _handleResetPasswordStep = async (
    step?: ResetPasswordOutput['nextStep'],
  ) => {
    if (!step) {
      router.push('/reset-password/confirm-code');
      return;
    }

    const _step = step.resetPasswordStep;
    switch (_step) {
      case 'CONFIRM_RESET_PASSWORD_WITH_CODE': {
        const codeDeliveryDetails = step.codeDeliveryDetails;
        console.log(
          `Confirmation code was sent to ${codeDeliveryDetails.deliveryMedium}`,
        );
        router.push('/reset-password/confirm-code');
        // Collect the confirmation code from the user and pass to confirmResetPassword.
        break;
      }
      case 'DONE':
        break;
    }
  };

  /**
   * エラーハンドリング
   * @param error キャッチしたエラーオブジェクト
   * @param procName 任意の処理フロー識別名
   */
  const _getJpErrMessage = (error: any, procName: string) => {
    switch (error.name) {
      case 'UserNotConfirmedException':
        // ユーザのステータスが UNCONFIRMED の場合に起こる。
        // SignUp用のコードを再送し、ステータスを CONFIRMED にする必要がある。
        return 'メールアドレスの検証が完了していません。';
      case 'PasswordResetRequiredException':
        // Cognito コンソールでパスワードをリセット（ユーザープールにユーザをインポートする場合も含む）した場合に起こる。
        // パスワードをリセットする必要がある。
        return 'パスワードをリセットする必要があります。';
      case 'NotAuthorizedException':
        // 誤ったパスワードを入力した場合に起こる。
        // パスワードを間違え続けた場合にも起こり、error.message が 'Password attempts exceeded' になる。
        if (error.message == 'Password attempts exceeded') {
          return 'ログインの試行回数が上限を超えたため、一時的に認証できなくなりました。';
        } else {
          return 'メールアドレスまたはパスワードが正しくありません。';
        }
      case 'UserNotFoundException':
        return 'メールアドレスまたはパスワードが正しくありません。';
      case 'InvalidPasswordException':
        return 'パスワードは8文字以上の半角英数字で入力してください。';
      case 'InvalidParameterException':
        return '8文字以上の有効なパスワードを入力してください。';
      case 'LimitExceededException':
        return '試行回数が所定の回数を超えました。時間を置いて再度送信してください。';
      case 'UserAlreadyAuthenticatedException':
        return 'すでにログイン済みです。他のアカウントでログインする場合はマイページから一度ログアウトしてください。';
      case 'UsernameExistsException':
        return 'このメールアドレスはご使用できません。別のメールアドレスでお試しください。すでに登録済みの場合はログイン画面からログインしてください。';
      case 'CodeMismatchException':
        return '無効な認証コードが入力されました。再度確認してください。';
      case 'ExpiredCodeException':
        return '無効な認証コードが入力されました。有効期限を確認してください。';
      case 'CodeDeliveryFailureException':
        return '認証コードの送信に失敗しました。再度認証コードを送信してください。';
      case 'InvalidRefreshToken':
        return 'リフレッシュトークンが無効です。';
      case 'NetworkError':
        return 'インターネット接続に失敗しました。';
      default:
        // その他のエラー
        return (
          procName +
          '処理でエラーが発生しました。(' +
          error.name +
          ':' +
          error.message +
          ')'
        );
    }
  };

  return {
    errorMessage,
    handleSignUp,
    handleSignUpConfirmation,
    handleResendSignUpCode,
    handleSignIn,
    handleLineSignIn,
    handleAppleSignIn,
    handleGoogleSignIn,
    handleLineIntegration,
    handleAppleIntegration,
    handleGoogleIntegration,
    getAccountType,
    isSignedIn,
    getUserAttributes,
    getCognitoTokens,
    getAccessToken,
    getCurrentProviderId,
    checkPassword,
    handleUpdateAttribute,
    handleUpdateAttributeConfirmation,
    handleResetPassword,
    handleResetPasswordConfirmation,
    handleUpdatePassword,
    handleSignOut,
    handleUnsubscribe,
    forceRefreshSession,
    saveLoginMethodOnSuccess,
  };
};
